import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Currency } from '../types';
import { supabase } from '../supabase/client';

type CurrencyContextType = {
  currency: Currency;
  changeCurrency: (currency: Currency) => Promise<void>;
  formatPrice: (amount: number, currency?: Currency) => string;
  convertPrice: (amount: number, fromCurrency: Currency, toCurrency: Currency) => number;
};

const CURRENCY_STORAGE_KEY = 'cotizatrin_user_currency';
const DEFAULT_CURRENCY = Currency.MXN;

const CurrencyContext = createContext<CurrencyContextType>({
  currency: DEFAULT_CURRENCY,
  changeCurrency: async () => {},
  formatPrice: (amount: number) => `$${amount.toFixed(2)}`,
  convertPrice: (amount: number) => amount,
});

export const useCurrency = () => useContext(CurrencyContext);

interface CurrencyProviderProps {
  children: ReactNode;
}

const EXCHANGE_RATES: Record<Currency, number> = {
  [Currency.USD]: 1,
  [Currency.EUR]: 0.92,
  [Currency.MXN]: 20,
};

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  [Currency.USD]: '$',
  [Currency.EUR]: '€',
  [Currency.MXN]: '$',
};

const getSavedCurrency = (): Currency => {
  try {
    const savedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (savedCurrency && Object.values(Currency).includes(savedCurrency as Currency)) {
      return savedCurrency as Currency;
    }
  } catch (error) {
    console.warn('Error reading saved currency from localStorage:', error);
  }
  return DEFAULT_CURRENCY;
};

const saveCurrencyToStorage = (currency: Currency): void => {
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  } catch (error) {
    console.warn('Error saving currency to localStorage:', error);
  }
};

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrency] = useState<Currency>(getSavedCurrency());
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (user) {
          const { data: userData } = await supabase
            .from('users')
            .select('preferred_currency')
            .eq('id', user.id)
            .maybeSingle();

          if (userData?.preferred_currency) {
            const dbCurrency = userData.preferred_currency as Currency;
            setCurrency(dbCurrency);
            saveCurrencyToStorage(dbCurrency);
          }
        }
      } catch (error) {
        console.warn('Error loading currency preference from database:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeCurrency();
  }, []);

  const changeCurrency = async (newCurrency: Currency) => {
    setCurrency(newCurrency);
    saveCurrencyToStorage(newCurrency);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        await supabase
          .from('users')
          .update({ preferred_currency: newCurrency })
          .eq('id', user.id);
      }
    } catch (error) {
      console.warn('Error saving currency preference to database:', error);
    }

    window.dispatchEvent(new CustomEvent('currency-changed', { detail: newCurrency }));
  };

  const roundToPsychologicalPrice = (amount: number, targetCurrency: Currency): number => {
    if (targetCurrency === Currency.MXN) {
      return amount;
    }

    return Math.ceil(amount) - 0.01;
  };

  const convertPrice = (amount: number, fromCurrency: Currency, toCurrency: Currency): number => {
    if (fromCurrency === toCurrency) return amount;

    const amountInUSD = amount / EXCHANGE_RATES[fromCurrency];
    const convertedAmount = amountInUSD * EXCHANGE_RATES[toCurrency];

    return roundToPsychologicalPrice(convertedAmount, toCurrency);
  };

  const formatPrice = (amount: number, targetCurrency?: Currency): string => {
    const currencyToUse = targetCurrency || currency;
    const symbol = CURRENCY_SYMBOLS[currencyToUse];

    return `${symbol}${amount.toFixed(2)}`;
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <CurrencyContext.Provider value={{ currency, changeCurrency, formatPrice, convertPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
